import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function BrandingResourcesPage() {
  const brandElements = [
    {
      title: 'Brand Identity',
      icon: 'ri-fingerprint-line',
      description: 'Define who you are as a business',
      components: [
        'Mission statement - Your business purpose and values',
        'Vision statement - Where you want your business to go',
        'Core values - Principles that guide your decisions',
        'Brand personality - Human characteristics of your brand',
        'Unique value proposition - What sets you apart',
        'Target audience definition - Who you serve in El Paso'
      ]
    },
    {
      title: 'Visual Identity',
      icon: 'ri-palette-line',
      description: 'Create a memorable visual presence',
      components: [
        'Logo design - Primary and secondary versions',
        'Color palette - 2-4 primary colors with hex codes',
        'Typography - Font families for headers and body text',
        'Imagery style - Photography and illustration guidelines',
        'Icon set - Consistent iconography system',
        'Layout templates - Business cards, letterhead, presentations'
      ]
    },
    {
      title: 'Brand Voice',
      icon: 'ri-message-3-line',
      description: 'Communicate consistently across all channels',
      components: [
        'Tone of voice - Professional, friendly, authoritative, etc.',
        'Language style - Formal vs. casual, technical vs. simple',
        'Key messaging - Core messages to communicate',
        'Tagline or slogan - Memorable phrase representing your brand',
        'Storytelling approach - How you share your brand story',
        'Bilingual considerations - English/Spanish for El Paso market'
      ]
    },
    {
      title: 'Brand Guidelines',
      icon: 'ri-book-open-line',
      description: 'Document standards for consistent application',
      components: [
        'Logo usage rules - Spacing, sizing, placement',
        'Color specifications - Print (CMYK) and digital (RGB/HEX)',
        'Typography hierarchy - When to use each font',
        'Do\'s and don\'ts - Examples of correct and incorrect usage',
        'Application examples - Website, social media, print materials',
        'Brand voice examples - Sample copy in your brand voice'
      ]
    }
  ];

  const designTools = [
    {
      name: 'Canva',
      description: 'User-friendly design platform for non-designers',
      price: 'Free - $15/month',
      icon: 'ri-image-edit-line'
    },
    {
      name: 'Adobe Creative Cloud',
      description: 'Professional design suite (Photoshop, Illustrator, InDesign)',
      price: '$54.99/month',
      icon: 'ri-brush-line'
    },
    {
      name: 'Figma',
      description: 'Collaborative interface design tool',
      price: 'Free - $15/user/month',
      icon: 'ri-layout-line'
    },
    {
      name: 'Looka',
      description: 'AI-powered logo maker',
      price: '$20 - $65 one-time',
      icon: 'ri-magic-line'
    },
    {
      name: 'Coolors',
      description: 'Color palette generator',
      price: 'Free',
      icon: 'ri-palette-line'
    },
    {
      name: 'Google Fonts',
      description: 'Free font library',
      price: 'Free',
      icon: 'ri-font-size-2'
    }
  ];

  const localAgencies = [
    {
      name: 'El Paso Design Studios',
      services: 'Full-service branding and graphic design',
      specialty: 'Local business branding'
    },
    {
      name: 'Borderplex Creative',
      services: 'Brand strategy and visual identity',
      specialty: 'Bilingual brand development'
    },
    {
      name: 'Desert Design Co.',
      services: 'Logo design and brand guidelines',
      specialty: 'Startup branding packages'
    },
    {
      name: 'UTEP Innovation Hub',
      services: 'Student design services at reduced rates',
      specialty: 'Budget-friendly options'
    }
  ];

  const brandingSteps = [
    {
      step: '1',
      title: 'Research & Discovery',
      description: 'Analyze competitors, understand your target audience, and identify market gaps in El Paso',
      duration: '1-2 weeks'
    },
    {
      step: '2',
      title: 'Strategy Development',
      description: 'Define your brand positioning, personality, and key messaging',
      duration: '1 week'
    },
    {
      step: '3',
      title: 'Visual Identity Design',
      description: 'Create logo, select colors and fonts, develop visual style',
      duration: '2-3 weeks'
    },
    {
      step: '4',
      title: 'Brand Guidelines',
      description: 'Document all brand standards in a comprehensive guide',
      duration: '1 week'
    },
    {
      step: '5',
      title: 'Implementation',
      description: 'Apply branding across all touchpoints - website, social media, print materials',
      duration: '2-4 weeks'
    },
    {
      step: '6',
      title: 'Launch & Monitor',
      description: 'Introduce your brand to the market and gather feedback',
      duration: 'Ongoing'
    }
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Branding Resources",
        "item": "https://elpasotexasbusiness.com/resources/branding-resources"
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Branding Resources for El Paso Businesses - Logo, Identity & Design"
        description="Build a strong brand for your El Paso business. Access logo design tools, brand identity guides, color psychology, typography tips, and local designers to create memorable branding."
        canonical="https://elpasotexasbusiness.com/resources/branding-resources"
        ogImage="https://public.readdy.ai/ai/img_res/923527a183234193916527d871629d4b.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6">
              <div className="mb-8">
                <Link to="/resources" className="text-[#D2691E] hover:text-[#B8571A] font-semibold flex items-center space-x-2 whitespace-nowrap">
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to Resources</span>
                </Link>
              </div>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16">
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Branding Resources</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Build a strong, memorable brand identity that resonates with El Paso customers. Learn how to create a cohesive brand that stands out in the local market and builds lasting customer loyalty.
              </p>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Essential Brand Elements</h2>
              <div className="space-y-12">
                {brandElements.map((element, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-10">
                    <div className="flex items-start space-x-6 mb-6">
                      <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${element.icon} text-3xl text-white`}></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{element.title}</h3>
                        <p className="text-base text-gray-600">{element.description}</p>
                      </div>
                    </div>
                    <div className="ml-22">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">Key Components:</h4>
                      <ul className="space-y-3">
                        {element.components.map((component, compIndex) => (
                          <li key={compIndex} className="flex items-start space-x-3">
                            <i className="ri-checkbox-circle-fill text-[#D2691E] text-xl flex-shrink-0 mt-0.5"></i>
                            <span className="text-sm text-gray-700">{component}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Branding Process Timeline</h2>
              <div className="space-y-6">
                {brandingSteps.map((item, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 flex items-start space-x-6">
                    <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-bold text-white">{item.step}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                        <span className="text-sm font-semibold text-[#D2691E] whitespace-nowrap">{item.duration}</span>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Design Tools & Resources</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {designTools.map((tool, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                      <i className={`${tool.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{tool.name}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{tool.description}</p>
                    <div className="text-sm font-semibold text-[#D2691E]">{tool.price}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Local El Paso Branding Agencies</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {localAgencies.map((agency, index) => (
                  <div key={index} className="bg-[#1B3A3A] rounded-2xl p-8 text-white">
                    <h3 className="text-xl font-bold mb-3">{agency.name}</h3>
                    <p className="text-sm text-white/80 mb-2">{agency.services}</p>
                    <div className="flex items-center space-x-2 text-sm">
                      <i className="ri-star-fill text-[#D2691E]"></i>
                      <span className="text-white/90 font-semibold">{agency.specialty}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 bg-[#FAFAF8] rounded-2xl p-8">
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong className="text-gray-900">Note:</strong> These are example agencies. Research and compare multiple providers to find the best fit for your budget and needs. Request portfolios and references before making a decision.
                </p>
              </div>
            </div>

            <div className="bg-[#FAFAF8] rounded-3xl p-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Branding Best Practices</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  'Keep it simple - Memorable brands are easy to understand',
                  'Be consistent - Use the same branding across all platforms',
                  'Know your audience - Design for El Paso demographics',
                  'Tell your story - Share what makes your business unique',
                  'Be authentic - Don\'t copy competitors, be yourself',
                  'Think long-term - Avoid trendy designs that date quickly',
                  'Test with customers - Get feedback before finalizing',
                  'Protect your brand - Register trademarks when appropriate',
                  'Stay flexible - Allow room for growth and evolution',
                  'Invest wisely - Good branding pays off over time'
                ].map((tip, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <i className="ri-lightbulb-flash-line text-[#D2691E] text-2xl flex-shrink-0"></i>
                    <span className="text-base text-gray-700 pt-0.5">{tip}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-16 bg-[#1B3A3A] rounded-3xl p-12 text-center">
              <h3 className="text-4xl font-bold text-white mb-4">Ready to Build Your Brand?</h3>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Get personalized guidance on creating a brand that connects with El Paso customers.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg whitespace-nowrap"
              >
                <span>Contact Us</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}